import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, Youtube, MessageCircle, ArrowRight } from 'lucide-react';
import { siteConfig } from '../mock/data';
import { trackClick } from '../lib/tracking';
import { useSettings, useLogoFooter } from '../context/SettingsContext';
import { mergeFooter } from '../lib/footerDefaults';
import { getProductCategories } from '../lib/productCategories';

export default function Footer() {
  const { settings } = useSettings();
  const { logo: logoFooter, loading: logoLoading } = useLogoFooter();
  const [logoFailed, setLogoFailed] = React.useState(false);
  const f = mergeFooter(settings?.page_builder?.footer);
  const productCats = getProductCategories(settings);
  const logoSrc = logoFooter
    ? `${logoFooter}${logoFooter.includes('?') ? '&' : '?'}v=${encodeURIComponent(settings.updated_at || '')}`
    : '';

  const waLink = `https://api.whatsapp.com/send?phone=${settings.whatsapp_number || siteConfig.whatsappNumber}`;
  const brandName = settings.brand_name || 'BelBel';
  const brandFull = settings.brand_full || 'Sidoarjo Powder';
  const instagram = settings.instagram || siteConfig.instagram;
  const email = settings.email || siteConfig.email;
  const address = settings.address || 'Jl. Raya Sidoarjo - Krian Km 10, Sidoarjo, Jawa Timur 61257';
  const wa = settings.whatsapp_number || siteConfig.whatsappNumber;
  const showLogo = logoSrc && !logoFailed;
  const navItems = Array.isArray(settings.navigation) && settings.navigation.length
    ? settings.navigation
    : [
        { label: 'Beranda', path: '/' },
        { label: 'Tentang BelBel', path: '/tentang' },
        { label: 'Produk', path: '/produk' },
        { label: 'Agen & Cabang', path: '/agen' },
        { label: 'Artikel', path: '/artikel' },
        { label: 'Kontak', path: '/kontak' },
      ];

  React.useEffect(() => { setLogoFailed(false); }, [logoSrc]);

  return (
    <footer className="bg-japo-green-dark text-white pt-16 pb-6 mt-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5 min-h-[48px]">
              {showLogo ? (
                <img
                  src={logoSrc}
                  alt={brandName}
                  width={180}
                  height={48}
                  decoding="async"
                  className="h-12 w-auto max-w-[180px] object-contain"
                  onError={() => setLogoFailed(true)}
                />
              ) : logoLoading ? (
                <div className="h-12 w-32 rounded-lg bg-white/10 animate-pulse" aria-hidden />
              ) : (
                <>
                  <div className="w-12 h-12 rounded-xl bg-japo-yellow flex items-center justify-center shadow-lg">
                    <span className="text-japo-green-dark font-extrabold text-2xl">{brandName[0]}</span>
                  </div>
                  <div>
                    <div className="font-extrabold text-xl">{brandName}</div>
                    <div className="text-xs uppercase tracking-widest text-white/70 font-semibold">{brandFull}</div>
                  </div>
                </>
              )}
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-5 whitespace-pre-line">{f.about}</p>
            <div className="flex gap-3">
              <a href={instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-japo-yellow hover:text-japo-green-dark flex items-center justify-center transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              {f.facebook ? (
                <a href={f.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-japo-yellow hover:text-japo-green-dark flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              ) : null}
              {f.youtube ? (
                <a href={f.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-japo-yellow hover:text-japo-green-dark flex items-center justify-center transition-colors">
                  <Youtube className="w-4 h-4" />
                </a>
              ) : null}
              <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackClick('whatsapp_footer')} className="w-9 h-9 rounded-full bg-white/10 hover:bg-japo-yellow hover:text-japo-green-dark flex items-center justify-center transition-colors">
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-base mb-5 relative pb-3">
              {f.quick_title}
              <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-japo-yellow"></span>
            </h4>
            <ul className="space-y-3 text-sm">
              {navItems.map((item) => (
                <li key={item.path + item.label}>
                  <Link to={item.path} className="text-white/70 hover:text-japo-yellow transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-base mb-5 relative pb-3">
              {f.categories_title}
              <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-japo-yellow"></span>
            </h4>
            <ul className="space-y-3 text-sm">
              {productCats.map((c) => (
                <li key={c.id}>
                  <Link to={`/produk?kategori=${c.id}`} className="text-white/70 hover:text-japo-yellow transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-base mb-5 relative pb-3">
              {f.contact_title}
              <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-japo-yellow"></span>
            </h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-japo-yellow flex-shrink-0" />
                <span>{address}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="w-4 h-4 text-japo-yellow flex-shrink-0" />
                <a href={`tel:+${wa}`} className="hover:text-japo-yellow">+{wa}</a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="w-4 h-4 text-japo-yellow flex-shrink-0" />
                <a href={`mailto:${email}`} className="hover:text-japo-yellow">{email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex justify-center md:justify-start">
          <p className="text-white/60 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} {f.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
